// src/components/AdminDashboard/AdminDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import courtService from '../../../services/courtService';
import { Snackbar, Alert } from '@mui/material';
import qs from 'qs';

import DiscountCodesSection        from './sections/DiscountCodesSection';
import UserRolesSection            from './sections/UserRolesSection';
import AdminUserManagement         from './sections/AdminUserManagement';
import AddCourtContainer           from './sections/AddCourtContainer';
import CourtOwnershipSection       from './sections/CourtOwnershipSection';
import CourtCoOwnerSection         from './sections/CourtCoOwnerSection';
import PurchasesAfterInfluencerSection from './sections/PurchasesAfterInfluencerSection';
import UserPurchasesSection        from './sections/UserPurchasesSection';
import RevenueRecordsExportSection from './sections/RevenueRecordsExportSection';
import FeedbackManagementSection   from './sections/FeedbackManagementSection';
import UserFootageSection          from './sections/UserFootageSection';
import ReportingSection            from './sections/ReportingSection';
import AdminSubscriptionManagement from '../../subscription/AdminSubscriptionManagement';
import SubscriptionFeatureConfig   from '../../subscription/SubscriptionFeatureConfig';
import NotificationTestPanel       from '../../notifications/NotificationTestPanel';

/* ─── Navigation structure ────────────────────────────────────────────────── */
const NAV = [
  {
    group: 'Analytics',
    icon: '📊',
    items: [
      { key: 'purchases', label: 'User Purchases' },
      { key: 'revenue',   label: 'Revenue Records' },
      { key: 'reports',   label: 'Reports' },
    ],
  },
  {
    group: 'Users',
    icon: '👥',
    items: [
      { key: 'userManagement', label: 'User Management' },
      { key: 'roles',          label: 'User Roles' },
      { key: 'feedback',       label: 'Feedback' },
      { key: 'footage',        label: 'User Footage' },
    ],
  },
  {
    group: 'Business',
    icon: '🏢',
    items: [
      { key: 'discounts', label: 'Discount Codes' },
      { key: 'courts',    label: 'Courts' },
    ],
  },
  {
    group: 'System',
    icon: '⚙️',
    items: [
      { key: 'subscription',  label: 'Subscriptions' },
      { key: 'featureConfig', label: 'Feature Config' },
      { key: 'notifications', label: 'Notification Tests' },
    ],
  },
];

/* ─── Sidebar nav item ────────────────────────────────────────────────────── */
const NavItem = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={[
      'w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 flex items-center gap-2',
      active
        ? 'bg-brand-red text-white font-semibold shadow-sm'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    ].join(' ')}
  >
    {active && <span className="w-1 h-1 rounded-full bg-white shrink-0" />}
    {label}
  </button>
);

/* ─── Toast helper ────────────────────────────────────────────────────────── */
const Toast = ({ open, onClose, severity, children }) => (
  <Snackbar open={open} autoHideDuration={4000} onClose={onClose}>
    <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>{children}</Alert>
  </Snackbar>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const AdminDashboard = () => {
  /* ── state ── */
  const [tab, setTab] = useState('purchases');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [discountCodes,     setDiscountCodes]     = useState([]);
  const [loadingDiscountCodes, setLoadingDiscountCodes] = useState(true);
  const [deleteError,       setDeleteError]       = useState('');
  const [deleteSuccess,     setDeleteSuccess]     = useState('');

  const [users,            setUsers]            = useState([]);
  const [loadingUsers,     setLoadingUsers]     = useState(true);
  const [selectedUser,     setSelectedUser]     = useState('');
  const [selectedRole,     setSelectedRole]     = useState('');
  const [loadingAssign,    setLoadingAssign]    = useState(false);
  const [assignSuccess,    setAssignSuccess]    = useState('');
  const [assignError,      setAssignError]      = useState('');

  const [courts,               setCourts]               = useState([]);
  const [selectedCourt,        setSelectedCourt]        = useState('');
  const [selectedCourtOwner,   setSelectedCourtOwner]   = useState('');
  const [loadingAssignCourt,   setLoadingAssignCourt]   = useState(false);
  const [assignCourtSuccess,   setAssignCourtSuccess]   = useState('');
  const [assignCourtError,     setAssignCourtError]     = useState('');

  const [selectedCoOwnerCourt, setSelectedCoOwnerCourt] = useState('');
  const [selectedCoOwner,      setSelectedCoOwner]      = useState('');
  const [loadingAddCoOwner,    setLoadingAddCoOwner]    = useState(false);
  const [addCoOwnerSuccess,    setAddCoOwnerSuccess]    = useState('');
  const [addCoOwnerError,      setAddCoOwnerError]      = useState('');

  const [purchases,             setPurchases]             = useState([]);
  const [loadingPurchases,      setLoadingPurchases]      = useState(true);
  const [purchaseError,         setPurchaseError]         = useState('');
  const [purchasePage,          setPurchasePage]          = useState(0);
  const [purchaseTotalPages,    setPurchaseTotalPages]    = useState(0);
  const [purchaseTotalElements, setPurchaseTotalElements] = useState(0);
  const [purchaseSize,          setPurchaseSize]          = useState(15);
  const [purchaseSearchTerm,    setPurchaseSearchTerm]    = useState('');
  const [purchaseFilterCourt,   setPurchaseFilterCourt]   = useState('');
  const [purchaseFilterDateRange, setPurchaseFilterDateRange] = useState('all');

  const availablePurchaseCourts = useMemo(() => {
    const names = (courts || []).map((c) => c?.courtName || c?.name).filter(Boolean);
    return [...new Set(names)].sort();
  }, [courts]);

  const [feedbackList,      setFeedbackList]      = useState([]);
  const [loadingFeedback,   setLoadingFeedback]   = useState(true);
  const [feedbackError,     setFeedbackError]     = useState('');
  const [deleteFeedbackSuccess, setDeleteFeedbackSuccess] = useState('');

  const [selectedUserForPurchases, setSelectedUserForPurchases] = useState('');
  const [filteredPurchases,        setFilteredPurchases]        = useState([]);
  const [loadingFilteredPurchases, setLoadingFilteredPurchases] = useState(false);
  const [purchaseQueryError,       setPurchaseQueryError]       = useState('');

  const [exportError,    setExportError]    = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYear,   setSelectedYear]   = useState(new Date().getFullYear());

  /* ── data fetching ── */
  useEffect(() => {
    api.get('/admin/discount-codes/all')
      .then(r => setDiscountCodes(r.data))
      .catch(() => setDeleteError('Failed to fetch discount codes.'))
      .finally(() => setLoadingDiscountCodes(false));
  }, []);

  useEffect(() => {
    api.get('/users/admin')
      .then(r => setUsers(r.data.content || []))
      .catch(() => setAssignError('Failed to fetch users.'))
      .finally(() => setLoadingUsers(false));
  }, []);

  useEffect(() => {
    courtService.getAllCourts().then(setCourts).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingPurchases(true);
    setPurchaseError('');
    api.get('/users/admin/video-purchases', {
      params: {
        page: purchasePage, size: purchaseSize,
        sort: 'purchaseDate,desc',
        search: purchaseSearchTerm || undefined,
        courtName: purchaseFilterCourt || undefined,
        dateRange: purchaseFilterDateRange !== 'all' ? purchaseFilterDateRange : undefined,
      },
    })
      .then(res => {
        if (res.data.content) {
          setPurchases(res.data.content);
          setPurchaseTotalPages(res.data.totalPages || 0);
          setPurchaseTotalElements(res.data.totalElements || 0);
        } else {
          setPurchaseError('Failed to fetch user purchases.');
        }
      })
      .catch(err => {
        setPurchaseError(err.response?.data?.message || 'Failed to fetch user purchases.');
        setPurchases([]); setPurchaseTotalPages(0); setPurchaseTotalElements(0);
      })
      .finally(() => setLoadingPurchases(false));
  }, [purchasePage, purchaseSize, purchaseSearchTerm, purchaseFilterCourt, purchaseFilterDateRange]);

  useEffect(() => {
    api.get('/feedback/all')
      .then(r => setFeedbackList(r.data))
      .catch(() => setFeedbackError('Failed to fetch feedback.'))
      .finally(() => setLoadingFeedback(false));
  }, []);

  useEffect(() => {
    if (!selectedUserForPurchases) return;
    setLoadingFilteredPurchases(true);
    api.get(`/users/admin/${selectedUserForPurchases}/purchases/influencer-latest`)
      .then(r => setFilteredPurchases(r.data))
      .catch(() => setPurchaseQueryError('Failed to fetch purchases for selected user.'))
      .finally(() => setLoadingFilteredPurchases(false));
  }, [selectedUserForPurchases]);

  /* ── handlers ── */
  const handleAssignRole = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedRole) { setAssignError('Please select both a user and a role.'); return; }
    try {
      setLoadingAssign(true);
      const u = users.find(u => u.username === selectedUser);
      if (!u) throw new Error('User not found');
      await api.post(`/users/admin/${u.id}/roles/${selectedRole.toUpperCase()}`);
      setAssignSuccess(`Role "${selectedRole}" assigned to "${selectedUser}" successfully.`);
      const res = await api.get('/users/admin');
      setUsers(res.data.content || []);
      setSelectedUser(''); setSelectedRole('');
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to assign role.');
    } finally { setLoadingAssign(false); }
  };

  const handleAssignCourtOwnership = async (e) => {
    e.preventDefault();
    if (!selectedCourt || !selectedCourtOwner) { setAssignCourtError('Please select both a court and a user.'); return; }
    try {
      setLoadingAssignCourt(true);
      await courtService.assignCourtOwnership(selectedCourt, selectedCourtOwner);
      setAssignCourtSuccess(`Court ownership assigned to ${selectedCourtOwner}.`);
    } catch (err) {
      setAssignCourtError(err.response?.data?.message || 'Failed to assign court ownership.');
    } finally { setLoadingAssignCourt(false); setSelectedCourt(''); setSelectedCourtOwner(''); }
  };

  const handleAddCourtCoOwner = async (e) => {
    e.preventDefault();
    if (!selectedCoOwnerCourt || !selectedCoOwner) { setAddCoOwnerError('Please select both a court and a user.'); return; }
    try {
      setLoadingAddCoOwner(true);
      await courtService.addCourtCoOwner(selectedCoOwnerCourt, selectedCoOwner);
      setAddCoOwnerSuccess(`Co-owner ${selectedCoOwner} added to court successfully.`);
    } catch (err) {
      setAddCoOwnerError(err.response?.data?.message || 'Failed to add co-owner.');
    } finally { setLoadingAddCoOwner(false); setSelectedCoOwnerCourt(''); setSelectedCoOwner(''); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/discount-codes/${id}`);
      setDiscountCodes(prev => prev.filter(c => c.id !== id));
      setDeleteSuccess('Discount code deleted successfully.');
    } catch { setDeleteError('Failed to delete discount code.'); }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    try {
      await api.delete(`/feedback/delete/${feedbackId}`);
      setFeedbackList(prev => prev.filter(fb => fb.id !== feedbackId));
      setDeleteFeedbackSuccess('Feedback deleted successfully.');
    } catch { setFeedbackError('Failed to delete feedback.'); }
  };

  const handleExportRevenue = async () => {
    try {
      setExportError('');
      const res = await api.get('/users/admin/purchases/export', {
        responseType: 'arraybuffer',
        params: { months: selectedMonths, year: selectedYear },
        paramsSerializer: p => qs.stringify(p, { arrayFormat: 'repeat' }),
      });
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', 'purchases.xlsx');
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setExportError('Failed to export revenue records.'); }
  };

  /* ── active section label for the mobile breadcrumb ── */
  const activeLabel = NAV.flatMap(g => g.items).find(i => i.key === tab)?.label ?? '';

  /* ── render ── */
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 px-6 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">Admin Dashboard</h1>
          <p className="text-xs text-gray-400 mt-0.5">Manage your platform</p>
        </div>
        {/* Mobile: show current section + hamburger */}
        <button
          onClick={() => setSidebarOpen(p => !p)}
          className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium"
        >
          <span>{activeLabel}</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ────────────────────────────────────────────────────── */}
        {/* Overlay (mobile) */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <aside className={[
          'bg-white border-r border-gray-100 w-56 shrink-0 flex flex-col overflow-y-auto',
          'fixed top-0 bottom-0 z-40 transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}>
          <div className="px-4 pt-6 pb-4 border-b border-gray-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Admin Panel</p>
          </div>
          <nav className="flex-1 p-3 space-y-5 overflow-y-auto">
            {NAV.map(({ group, icon, items }) => (
              <div key={group}>
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5">
                  <span>{icon}</span> {group}
                </p>
                <div className="space-y-0.5">
                  {items.map(({ key, label }) => (
                    <NavItem
                      key={key}
                      label={label}
                      active={tab === key}
                      onClick={() => { setTab(key); setSidebarOpen(false); }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* ── Main content ───────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900">{activeLabel}</h2>
            <div className="w-10 h-0.5 bg-brand-red mt-1 rounded-full" />
          </div>

          {/* ── Analytics ── */}
          {tab === 'purchases' && (
            <>
              <PurchasesAfterInfluencerSection
                users={users}
                selectedUserForPurchases={selectedUserForPurchases}
                setSelectedUserForPurchases={setSelectedUserForPurchases}
                filteredPurchases={filteredPurchases}
                loadingFilteredPurchases={loadingFilteredPurchases}
                purchaseQueryError={purchaseQueryError}
              />
              <UserPurchasesSection
                purchases={purchases}
                loadingPurchases={loadingPurchases}
                purchaseError={purchaseError}
                currentPage={purchasePage + 1}
                totalPages={purchaseTotalPages}
                totalElements={purchaseTotalElements}
                pageSize={purchaseSize}
                onPageChange={p => setPurchasePage(p - 1)}
                onPageSizeChange={s => { setPurchasePage(0); setPurchaseSize(s); }}
                searchTerm={purchaseSearchTerm}
                onSearchTermChange={v => { setPurchasePage(0); setPurchaseSearchTerm(v); }}
                filterCourt={purchaseFilterCourt}
                onFilterCourtChange={v => { setPurchasePage(0); setPurchaseFilterCourt(v); }}
                filterDateRange={purchaseFilterDateRange}
                onFilterDateRangeChange={v => { setPurchasePage(0); setPurchaseFilterDateRange(v); }}
                availableCourts={availablePurchaseCourts}
                onClearFilters={() => { setPurchasePage(0); setPurchaseSearchTerm(''); setPurchaseFilterCourt(''); setPurchaseFilterDateRange('all'); }}
              />
            </>
          )}
          {tab === 'revenue' && (
            <RevenueRecordsExportSection
              selectedMonths={selectedMonths} selectedYear={selectedYear}
              handleYearChange={e => setSelectedYear(e.target.value)}
              handleMonthChange={e => setSelectedMonths(e.target.value)}
              handleExportRevenue={handleExportRevenue} exportError={exportError}
            />
          )}
          {tab === 'reports' && <ReportingSection />}

          {/* ── Users ── */}
          {tab === 'userManagement' && <AdminUserManagement />}
          {tab === 'roles' && (
            <UserRolesSection
              users={users} loadingUsers={loadingUsers}
              selectedUser={selectedUser} selectedRole={selectedRole}
              setSelectedUser={setSelectedUser} setSelectedRole={setSelectedRole}
              handleAssignRole={handleAssignRole} loadingAssign={loadingAssign}
              assignError={assignError} assignSuccess={assignSuccess}
            />
          )}
          {tab === 'feedback' && (
            <FeedbackManagementSection
              feedbackList={feedbackList} loadingFeedback={loadingFeedback}
              feedbackError={feedbackError} handleDeleteFeedback={handleDeleteFeedback}
            />
          )}
          {tab === 'footage' && <UserFootageSection />}

          {/* ── Business ── */}
          {tab === 'discounts' && (
            <DiscountCodesSection
              discountCodes={discountCodes} loadingDiscountCodes={loadingDiscountCodes}
              deleteError={deleteError} handleDelete={handleDelete}
            />
          )}
          {tab === 'courts' && (
            <>
              <AddCourtContainer />
              <CourtOwnershipSection
                courts={courts} users={users} loadingUsers={loadingUsers}
                selectedCourt={selectedCourt} setSelectedCourt={setSelectedCourt}
                selectedCourtOwner={selectedCourtOwner} setSelectedCourtOwner={setSelectedCourtOwner}
                handleAssignCourtOwnership={handleAssignCourtOwnership} loadingAssignCourt={loadingAssignCourt}
              />
              <CourtCoOwnerSection
                courts={courts} users={users} loadingUsers={loadingUsers}
                selectedCourt={selectedCoOwnerCourt} setSelectedCourt={setSelectedCoOwnerCourt}
                selectedCoOwner={selectedCoOwner} setSelectedCoOwner={setSelectedCoOwner}
                handleAddCourtCoOwner={handleAddCourtCoOwner} loadingAddCoOwner={loadingAddCoOwner}
              />
            </>
          )}

          {/* ── System ── */}
          {tab === 'subscription'  && <AdminSubscriptionManagement />}
          {tab === 'featureConfig' && <SubscriptionFeatureConfig />}
          {tab === 'notifications' && <NotificationTestPanel />}
        </main>
      </div>

      {/* ── Toasts ─────────────────────────────────────────────────────── */}
      <Toast open={!!deleteSuccess}        onClose={() => setDeleteSuccess('')}        severity="success">{deleteSuccess}</Toast>
      <Toast open={!!deleteError}          onClose={() => setDeleteError('')}          severity="error">{deleteError}</Toast>
      <Toast open={!!assignSuccess}        onClose={() => setAssignSuccess('')}        severity="success">{assignSuccess}</Toast>
      <Toast open={!!assignError}          onClose={() => setAssignError('')}          severity="error">{assignError}</Toast>
      <Toast open={!!deleteFeedbackSuccess} onClose={() => setDeleteFeedbackSuccess('')} severity="success">{deleteFeedbackSuccess}</Toast>
      <Toast open={!!feedbackError}        onClose={() => setFeedbackError('')}        severity="error">{feedbackError}</Toast>
      <Toast open={!!assignCourtSuccess}   onClose={() => setAssignCourtSuccess('')}   severity="success">{assignCourtSuccess}</Toast>
      <Toast open={!!assignCourtError}     onClose={() => setAssignCourtError('')}     severity="error">{assignCourtError}</Toast>
      <Toast open={!!addCoOwnerSuccess}    onClose={() => setAddCoOwnerSuccess('')}    severity="success">{addCoOwnerSuccess}</Toast>
      <Toast open={!!addCoOwnerError}      onClose={() => setAddCoOwnerError('')}      severity="error">{addCoOwnerError}</Toast>
    </div>
  );
};

export default AdminDashboard;
